import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Download, FileText, Search, Filter, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const WINDOWS = [
  { v: 7, l: "7 days" },
  { v: 30, l: "30 days" },
  { v: 60, l: "60 days" },
  { v: 90, l: "90 days" },
];

export default function NGTrend() {
  const [windowDays, setWindowDays] = useState(30);
  const [supplierId, setSupplierId] = useState("");
  const [materialId, setMaterialId] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [data, setData] = useState({ rows: [], total_ng_events: 0 });
  const [q, setQ] = useState("");

  useEffect(() => {
    Promise.all([api.get("/suppliers"), api.get("/materials")]).then(([s, m]) => {
      setSuppliers(s.data); setMaterials(m.data);
    });
  }, []);

  const params = () => {
    const p = new URLSearchParams({ window: windowDays });
    if (supplierId) p.append("supplier_id", supplierId);
    if (materialId) p.append("material_id", materialId);
    return p.toString();
  };

  const load = async () => {
    try {
      const r = await api.get(`/reports/ng-trend?${params()}`);
      setData(r.data || { rows: [], total_ng_events: 0 });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [windowDays, supplierId, materialId]);

  const exportTo = async (kind) => {
    try {
      const r = await api.get(`/reports/ng-trend.${kind}?${params()}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data]));
      a.download = `ng_trend_${windowDays}d.${kind}`;
      a.click();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Export failed"); }
  };

  const filtered = useMemo(() => {
    if (!q) return data.rows;
    const k = q.toLowerCase();
    return data.rows.filter((r) =>
      (r.material_code || "").toLowerCase().includes(k) ||
      (r.material_name || "").toLowerCase().includes(k) ||
      (r.supplier_code || "").toLowerCase().includes(k) ||
      (r.supplier_name || "").toLowerCase().includes(k) ||
      (r.top_failing_params || "").toLowerCase().includes(k)
    );
  }, [data.rows, q]);

  // Highlight worst suppliers (>=3 NG in window)
  const watchlist = filtered.filter((r) => r.ng_count >= 3);

  return (
    <div data-testid="ng-trend-page">
      <PageHeader title="NG Trend Report" subtitle="Supplier quality scorecard — NG events per supplier · material"
        helpText="Aggregates Quality NG (Not Good) events from the inspection ledger. Combine with the Abnormal Report for full vendor performance review."
        right={
          <div className="flex gap-2">
            <button onClick={() => exportTo("xlsx")} data-testid="ng-export-xlsx" className="bg-[#2E7D32] text-white text-xs px-3 py-2 rounded-sm uppercase tracking-widest font-bold flex items-center gap-2"><Download size={12}/> Excel</button>
            <button onClick={() => exportTo("pdf")} data-testid="ng-export-pdf" className="bg-[#C62828] text-white text-xs px-3 py-2 rounded-sm uppercase tracking-widest font-bold flex items-center gap-2"><FileText size={12}/> PDF</button>
          </div>
        }
      />
      <div className="p-8 space-y-4">
        {/* Filters */}
        <div className="bg-white border border-[#EEEEEE] rounded-sm p-4">
          <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-widest font-bold text-[#0D2B5E]/60"><Filter size={12}/> Filters</div>
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mb-1">Window</label>
              <select value={windowDays} onChange={(e) => setWindowDays(+e.target.value)} data-testid="ng-window" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm">
                {WINDOWS.map((w) => <option key={w.v} value={w.v}>Last {w.l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mb-1">Supplier</label>
              <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} data-testid="ng-supplier" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm">
                <option value="">All suppliers</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.code} — {s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mb-1">Material</label>
              <select value={materialId} onChange={(e) => setMaterialId(e.target.value)} data-testid="ng-material" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm">
                <option value="">All materials</option>
                {materials.map((m) => <option key={m.id} value={m.id}>{m.code} — {m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mb-1">Search</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-2.5 text-[#0D2B5E]/40"/>
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter visible rows…" data-testid="ng-search" className="w-full pl-8 border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm"/>
              </div>
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-4">
          <KV k="Total NG events" v={data.total_ng_events} accent="#C62828"/>
          <KV k="Affected supplier×material pairs" v={data.rows.length} accent="#0D2B5E"/>
          <KV k="On watchlist (≥3 NG)" v={watchlist.length} accent="#E65100"/>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#EEEEEE] rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr>
                <th className="text-left px-4 py-2">Supplier</th>
                <th className="text-left px-4 py-2">Material</th>
                <th className="text-right px-4 py-2">NG Events</th>
                <th className="text-right px-4 py-2">Auto-NG</th>
                <th className="text-left px-4 py-2">Top Failing Params</th>
                <th className="text-left px-4 py-2">First → Last NG</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => {
                const watch = r.ng_count >= 3;
                return (
                  <tr key={i} className={watch ? "bg-[#C62828]/5" : (i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40")} data-testid={`ng-row-${i}`}>
                    <td className="px-4 py-2"><span className="font-mono text-[#1565C0]">{r.supplier_code}</span> {r.supplier_name}{watch && <AlertTriangle size={12} className="inline ml-2 text-[#C62828]"/>}</td>
                    <td className="px-4 py-2"><span className="font-mono text-[#1565C0]">{r.material_code}</span> {r.material_name}</td>
                    <td className="px-4 py-2 text-right font-mono font-bold text-[#C62828]">{r.ng_count}</td>
                    <td className="px-4 py-2 text-right font-mono text-[#E65100]">{r.auto_ng_count}</td>
                    <td className="px-4 py-2 text-xs text-[#0D2B5E]/70">{r.top_failing_params || "—"}</td>
                    <td className="px-4 py-2 text-[10px] tracking-widest font-mono text-[#0D2B5E]/60">{(r.first_ng_at || "").slice(0, 10)} → {(r.last_ng_at || "").slice(0, 10)}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center py-12 text-sm text-[#0D2B5E]/50">No NG events in this window. ✅</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {watchlist.length > 0 && (
          <div className="bg-[#C62828]/5 border-l-4 border-[#C62828] p-4 text-sm rounded-sm" data-testid="ng-watchlist-banner">
            <div className="font-bold text-[#C62828] uppercase tracking-widest text-xs mb-1 flex items-center gap-1"><AlertTriangle size={14}/> Supplier Watchlist</div>
            <div className="text-[#0D2B5E]/80">{watchlist.length} supplier×material pair{watchlist.length === 1 ? "" : "s"} crossed the 3-NG threshold within the last {windowDays} days. Consider holding next orders pending CAR or supplier audit.</div>
          </div>
        )}
      </div>
    </div>
  );
}

function KV({ k, v, accent = "#1565C0" }) {
  return (
    <div className="bg-white border border-[#EEEEEE] rounded-sm p-4 border-l-4" style={{ borderLeftColor: accent }}>
      <div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/50 font-bold">{k}</div>
      <div className="text-3xl font-mono font-bold mt-1" style={{ color: accent }}>{v}</div>
    </div>
  );
}
